import {
  MutationCache,
  QueryCache,
  QueryClient,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { reportError } from "../bugReporting";
import {
  getUpdatedSourceAccountsWithAdd,
  getUpdatedSourceAccountsWithDelete,
  getUpdatedSourceAccountsWithEdit,
} from "../sourceAccounts";
import {
  HTTPError,
  addBankRestriction,
  addBankSourceAccount,
  addCardRestriction,
  addCardSourceAccount,
  addPermissionToDicuss,
  addToThread,
  cancelPayment,
  cancelSeriesPayment,
  composeSecureMail,
  deleteSourceAccount,
  editBankSourceAccount,
  editCardSourceAccount,
  editOneTimePayment,
  editPermissionToDicuss,
  editSeriesPayment,
  enrollAutopay,
  getAccount,
  getAccountDetails,
  getAccountLockStatus,
  getAgentDetails,
  getAutopayEnrollment,
  getBankNames,
  getCardmemberMailList,
  getClassificationCount,
  getClassifications,
  getCreditLineIncreaseEligibility,
  getCycleToDateTransactions,
  getLostStolenOptions,
  getPartyRestrictions,
  getPaymentHistory,
  getPaymentMethods,
  getPaymentSeriesSchedule,
  getPaymentTransactions,
  getPermissionToDiscuss,
  getSecureMailList,
  getSecureMailThread,
  getStatementHistory,
  makeOneTimePayment,
  makeSeriesPayment,
  patchNextSecureMailId,
  patchSecureMailClassification,
  removeAutopayEnrollment,
  removePermissionToDiscuss,
  sendTestPushNotification,
  skipAutopay,
  submitLostStolen,
  submitCreditLineIncrease,
  updateAccountLockStatus,
  updateSecureMailStatus,
  updateUsername,
  verifyPermissionToDiscuss,
  viewMailAttachment,
  getCallInfo,
  getTerms,
  addPhone,
  editPhone,
  getPhones,
  verifyPhones,
  getPhoneOptions,
  getInactivationChangeMessages,
  accountSearch,
  accountSearchByThreeOfFour,
  getCitiesByZipcode,
  getCustomerInfo,
  getChangedHistory,
  getVerifiedHistory,
  getStatements,
  submitCardActivation,
  getCardActivationInfo,
  updateCustomerInfo,
  getPaperlessSettings,
  updatePaperlessSettings,
  getTextBankingServices,
  stopFteuService,
  stopFraudAlertService,
  getResultOptions,
  updatePermCollQueue,
  submitResultCsr,
  submitResultPayment,
  submitResultNoPayment,
  submitResultResearchOnly,
  getNoteDetails,
  getNotesSearch,
  addNote,
  getListen,
  callEnded,
} from "./endpoints";
import {
  AddBankSourceAccountRequest,
  AddCardSourceAccountRequest,
  AddNoteRequest,
  AddRestrictionRequest,
  AddToThreadRequest,
  CallEndedRequest,
  Channel,
  EditBankSourceAccountRequest,
  EditCardSourceAccountRequest,
  EnrollAutopayRequest,
  GetAutopayEnrollmentRequest,
  GetCallInfoRequest,
  GetCardActivationInfoResponse,
  GetCardmemberMailListRequest,
  GetChangedHistoryRequest,
  GetCreditLineIncreaseEligibilityRequest,
  GetCustomerInfoRequest,
  GetNotesSearchRequest,
  GetPaperlessSettingsRequest,
  GetPartyRestrictionsResponse,
  GetPaymentMethodsRequest,
  GetPaymentMethodsResponse,
  GetPaymentSeriesScheduleRequest,
  GetPaymentTransactionsRequest,
  GetPermissionToDiscussRequest,
  GetSecureMailListRequest,
  GetSecureMailThreadRequest,
  GetSecureMailThreadRequestKey,
  GetStatementsRequest,
  GetVerifiedHistoryRequest,
  MakeOneTimePaymentRequest,
  MakeSeriesPaymentRequest,
  PatchNextSecureMailIdRequest,
  PatchPaperlessSettingsRequest,
  PermissionToDiscussPermission,
  PermissionToDiscussRelationshipType,
  RESTRICTION_NONE,
  ResultCsrRequest,
  ResultNoPaymentRequest,
  ResultPaymentRequest,
  ResultResearchOnlyRequest,
  SendTestPushNotificationRequest,
  SkipRemoveAutopayRequest,
  SubmitLostStolenRequest,
  UpdateAccountLockStatusRequest,
  UpdatePermCollQueueRequest,
  UpdateSecureMailStatusRequest,
  UpdateUsernameRequest,
  ViewMailAttachmentRequest,
} from "./models";

const numericsOnlyRegex = /\D/g;
const getNotesSearchQueryKeyName = "getNotesSearch";

const strings = {
  defaultPhoneNumber: "0000000000",
};

// client
function reportNetworkErrors(error: unknown) {
  if (
    typeof error === "object" &&
    error !== null &&
    "expectedError" in error &&
    error.expectedError === true
  ) {
    return;
  }

  if (error instanceof Error) {
    const meta =
      error instanceof HTTPError ? { validation: error.validation } : {};
    reportError(error, meta);
    return;
  }

  reportError(new Error("Unknown query error"), {
    error,
  });
}
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: reportNetworkErrors,
  }),
  mutationCache: new MutationCache({
    onError: reportNetworkErrors,
  }),
});

// query keys
const queryKeys = {
  account: (dwbuid: string) => ["account", dwbuid] as const,
  accountDetails: (dwbuid: string) => ["accountDetails", dwbuid] as const,
  agentDetails: () => ["agentDetails"] as const,
  lock: (dwbuid: string) => ["login-lock", dwbuid] as const,
  paymentHistory: (dwbuid: string) => ["payment-history", dwbuid] as const,
  autopayEnrollment: ({ partyId, dwbuid }: GetAutopayEnrollmentRequest) =>
    ["autopay-enrollment", partyId, dwbuid] as const,
  classifications: () => ["classifications"] as const,
  classificationCount: () => ["classificationCount"] as const,
  secureMailList: (req: GetSecureMailListRequest) =>
    ["secure-mail-list", req.classificationId, req.page, req.size] as const,
  secureMail: () => ["secure-mail"] as const,
  secureMailThread: ({ secureMailId }: GetSecureMailThreadRequestKey) =>
    ["secure-mail-thread", secureMailId] as const,
  cardmemberMailList: ({ partyId, ...query }: GetCardmemberMailListRequest) => [
    "cardmemberMailList",
    partyId,
    query,
  ],
  // NOTE: Because the API requires dwbuid for payment transactions and other
  // queries that want to invalidate this query use partyId, we will just
  // naively invalidate ALL payment transactions queries.
  // This shouldn't have an impact unless agents are doing weird things like
  // having multiple windows open or switching back and forth between multiple
  // cardmembers.
  paymentTransactions: () => ["payment-transactions"],
  paymentMethods: (req: GetPaymentMethodsRequest) => [
    "payment-methods",
    req.partyId,
  ],
  partyRestrictions: (partyId: string) => ["party-restrictions", partyId],
  bankNames: (abaNumber: string) => ["bank-names", abaNumber],
  permissionToDiscuss: (req: GetPermissionToDiscussRequest) => [
    "permission-to-discuss",
    req.partyId,
    req.includeHistory,
    req.includeInactive,
  ],
  cycleToDateTransactions: (partyId: string) =>
    ["cycle-to-date-transactions", partyId] as const,
  statementHistory: (dwbuid: string) => ["statement-history", dwbuid] as const,
  lostStolenOptions: (dwbuid: string) =>
    ["lost-stolen-options", dwbuid] as const,
  creditLineHistoryEligibility: (
    req: GetCreditLineIncreaseEligibilityRequest,
  ) =>
    ["credit-line-increase-eligibility", req.dwbuid, req.referenceId] as const,
  callInfo: (req: GetCallInfoRequest) =>
    ["call-info", req.dwbuid, req.callId] as const,
  terms: (dwbuid: string) => ["terms", dwbuid] as const,
  phoneOptions: () => ["phone-options"] as const,
  phones: (dwbuid: string) => ["phones", dwbuid] as const,
  cities: (zipcode: string) => ["cities", zipcode] as const,
  customerInfo: (req: GetCustomerInfoRequest) =>
    ["customerInfo", req.dwbuid, req.referenceId] as const,
  changedHistory: (req: GetChangedHistoryRequest) =>
    [
      "changedHistory",
      req.dwbuid,
      req.referenceId,
      req.startDate,
      req.endDate,
    ] as const,
  verifiedHistory: (req: GetVerifiedHistoryRequest) =>
    [
      "verifiedHistory",
      req.dwbuid,
      req.referenceId,
      req.startDate,
      req.endDate,
    ] as const,
  statements: (req: GetStatementsRequest) =>
    ["statements", req.dwbuid, req.year] as const,
  paperlessSettings: (req: GetPaperlessSettingsRequest) => [
    "paperlessSettings",
    req.dwbuid,
  ],
  cardActivationInfo: (dwbuid: string) =>
    ["cardActivationInfo", dwbuid] as const,
  textBankingServices: (dwbuid: string) =>
    ["textBankingServices", dwbuid] as const,
  getResultOptions: (channel: Channel) =>
    ["getResultOptions", channel] as const,
  noteDetails: (dwbuid: string, noteId: string) =>
    ["noteDetails", dwbuid, noteId] as const,
  getNotesSearch: (req: GetNotesSearchRequest) => [
    getNotesSearchQueryKeyName,
    req.dwbuid,
    req.agent,
    req.channel,
    req.startDate,
    req.endDate,
    req.noteContent,
    req.notePriorityTypes,
    req.noteSourceType,
  ],
} as const;

// queries
export const useGetAgentDetails = () =>
  useQuery({
    queryKey: queryKeys.agentDetails(),
    queryFn: getAgentDetails,
  });

export const useGetAccount = (dwbuid: string) =>
  useQuery({
    enabled: !!dwbuid,
    queryKey: queryKeys.account(dwbuid),
    queryFn: () => getAccount(dwbuid),
  });

export const useGetAccountDetails = (dwbuid: string) =>
  useQuery({
    enabled: !!dwbuid,
    queryKey: queryKeys.accountDetails(dwbuid),
    queryFn: () => {
      return getAccountDetails(dwbuid);
    },
  });

export const useUpdateUsername = () =>
  useMutation({
    mutationFn: (params: UpdateUsernameRequest) => updateUsername(params),
    onSuccess: (_data, { dwbuid }) => {
      queryClient.invalidateQueries(queryKeys.accountDetails(dwbuid));
    },
  });

export const useSendTestPushNotification = () =>
  useMutation({
    mutationFn: (params: SendTestPushNotificationRequest) =>
      sendTestPushNotification(params),
  });

export const useGetAccountLockStatus = (dwbuid: string) =>
  useQuery({
    queryKey: queryKeys.lock(dwbuid),
    queryFn: () => getAccountLockStatus(dwbuid),
  });

export const useUpdateAccountLockStatus = () =>
  useMutation({
    mutationFn: (params: UpdateAccountLockStatusRequest) =>
      updateAccountLockStatus(params),
    onSuccess: (_data, { dwbuid }) => {
      const newData = { isLocked: false };
      queryClient.setQueryData(queryKeys.lock(dwbuid), newData);
      queryClient.invalidateQueries(queryKeys.accountDetails(dwbuid));
    },
  });

export const useGetPaymentHistory = (dwbuid: string) =>
  useQuery({
    queryKey: queryKeys.paymentHistory(dwbuid),
    queryFn: () => getPaymentHistory(dwbuid),
  });

export const useGetAutopayEnrollment = (req: GetAutopayEnrollmentRequest) =>
  useQuery({
    queryKey: queryKeys.autopayEnrollment(req),
    queryFn: () => getAutopayEnrollment(req),
  });

export const useGetSecureMailList = (
  req: GetSecureMailListRequest,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.secureMailList(req),
    queryFn: () => getSecureMailList(req),
    enabled,
    initialData: { total: 0, secureMail: [] },
  });

export const usePatchNextSecureMailId = () =>
  useMutation({
    mutationFn: (params: PatchNextSecureMailIdRequest) =>
      patchNextSecureMailId(params),
  });

export const usePatchSecureMailClassification = () =>
  useMutation({
    mutationFn: patchSecureMailClassification,
    onSuccess: (_, { secureMailId }) => {
      queryClient.invalidateQueries(
        queryKeys.secureMailThread({ secureMailId }),
      );
    },
  });

export const useGetClassifications = () =>
  useQuery({
    queryKey: queryKeys.classifications(),
    queryFn: () => getClassifications(),
  });

export const useGetClassificationCount = () =>
  useQuery({
    queryKey: queryKeys.classificationCount(),
    queryFn: () => getClassificationCount(),
  });

export const useGetCardmemberMailList = (
  req: GetCardmemberMailListRequest,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.cardmemberMailList(req),
    queryFn: () => getCardmemberMailList(req),
    enabled,
  });

export const useGetSecureMailThread = (
  req: GetSecureMailThreadRequest,
  enabled: boolean,
) =>
  useQuery({
    queryKey: queryKeys.secureMailThread(req),
    queryFn: () => getSecureMailThread(req),
    enabled,
  });

export const useAddToThread = () =>
  useMutation({
    mutationFn: (params: AddToThreadRequest) => addToThread(params),
    onSuccess: (_, req) =>
      queryClient.invalidateQueries(queryKeys.secureMailThread(req)),
  });

export const useComposeSecureMail = () =>
  useMutation({
    mutationFn: composeSecureMail,
  });

export const useUpdateSecureMailStatus = () =>
  useMutation({
    mutationFn: (params: UpdateSecureMailStatusRequest) =>
      updateSecureMailStatus(params),
    onSuccess: (_, { secureMailId }) =>
      queryClient.invalidateQueries(
        queryKeys.secureMailThread({ secureMailId }),
      ),
  });

export const useViewMailAttachment = () =>
  useMutation({
    mutationFn: (params: ViewMailAttachmentRequest) =>
      viewMailAttachment(params),
  });

export const useEnrollAutopay = () =>
  useMutation({
    mutationFn: (params: EnrollAutopayRequest) => enrollAutopay(params),
    onSuccess: (_data, { partyId, dwbuid }) => {
      queryClient.invalidateQueries(
        queryKeys.autopayEnrollment({ partyId, dwbuid }),
      );
      queryClient.invalidateQueries(queryKeys.accountDetails(dwbuid));
      queryClient.invalidateQueries([getNotesSearchQueryKeyName, dwbuid]);
    },
  });

export const useRemoveAutopayEnrollment = () =>
  useMutation({
    mutationFn: (params: SkipRemoveAutopayRequest) =>
      removeAutopayEnrollment(params),
    onSuccess: (_data, { partyId, dwbuid }) => {
      queryClient.invalidateQueries(
        queryKeys.autopayEnrollment({ partyId, dwbuid }),
      );
      queryClient.invalidateQueries(queryKeys.accountDetails(dwbuid));
      queryClient.invalidateQueries([getNotesSearchQueryKeyName, dwbuid]);
    },
  });

export const useSkipAutopay = () =>
  useMutation({
    mutationFn: (params: SkipRemoveAutopayRequest) => skipAutopay(params),
    onSuccess: (_data, { partyId, dwbuid }) => {
      queryClient.invalidateQueries(
        queryKeys.autopayEnrollment({ partyId, dwbuid }),
      );
      queryClient.invalidateQueries([getNotesSearchQueryKeyName, dwbuid]);
    },
  });

export const useGetPaymentTransactions = (req: GetPaymentTransactionsRequest) =>
  useQuery({
    // NOTE: See note with queryKeys.paymentTransactions
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.paymentTransactions(),
    queryFn: () => getPaymentTransactions(req),
  });

export const useGetPaymentMethods = (req: GetPaymentMethodsRequest) =>
  useQuery({
    queryKey: queryKeys.paymentMethods(req),
    queryFn: () => getPaymentMethods(req),
  });

export const useDeleteSourceAccount = () =>
  useMutation({
    mutationFn: (params: { partyId: string; sourceAccountId: string }) =>
      deleteSourceAccount(params),
    onSuccess: (_, { partyId, sourceAccountId }) => {
      queryClient.setQueryData(
        queryKeys.paymentMethods({ partyId }),
        (oldData: GetPaymentMethodsResponse | undefined) => {
          if (oldData === undefined) {
            return undefined;
          }

          return getUpdatedSourceAccountsWithDelete(sourceAccountId, oldData);
        },
      );
      queryClient.invalidateQueries(queryKeys.paymentMethods({ partyId }));
    },
  });

export const useAddBankSourceAccount = () =>
  useMutation({
    mutationFn: (params: AddBankSourceAccountRequest) =>
      addBankSourceAccount(params),
    onSuccess: (newBankSourceAccount, { partyId }) => {
      queryClient.setQueryData(
        queryKeys.paymentMethods({ partyId }),
        (oldData: GetPaymentMethodsResponse | undefined) => {
          if (oldData === undefined) {
            return undefined;
          }

          return getUpdatedSourceAccountsWithAdd(newBankSourceAccount, oldData);
        },
      );
      queryClient.invalidateQueries(queryKeys.paymentMethods({ partyId }));
    },
  });

export const useEditBankSourceAccount = () =>
  useMutation({
    mutationFn: (params: EditBankSourceAccountRequest) =>
      editBankSourceAccount(params),
    onSuccess: (editedBankSourceAccount, params) => {
      queryClient.setQueryData(
        queryKeys.paymentMethods({ partyId: params.partyId }),
        (oldData: GetPaymentMethodsResponse | undefined) => {
          if (oldData === undefined) {
            return;
          }

          return getUpdatedSourceAccountsWithEdit(
            editedBankSourceAccount,
            params.sourceAccountId,
            oldData,
          );
        },
      );
      queryClient.invalidateQueries(
        queryKeys.paymentMethods({ partyId: params.partyId }),
      );
    },
  });

export const useAddCardSourceAccount = () =>
  useMutation({
    mutationFn: (params: AddCardSourceAccountRequest) =>
      addCardSourceAccount(params),
    onSuccess: (newCardSourceAccount, { partyId }) => {
      queryClient.setQueryData(
        queryKeys.paymentMethods({ partyId }),
        (oldData: GetPaymentMethodsResponse | undefined) => {
          if (oldData === undefined) {
            return undefined;
          }

          return getUpdatedSourceAccountsWithAdd(newCardSourceAccount, oldData);
        },
      );
      queryClient.invalidateQueries(queryKeys.paymentMethods({ partyId }));
    },
  });

export const useEditCardSourceAccount = () =>
  useMutation({
    mutationFn: (params: EditCardSourceAccountRequest) =>
      editCardSourceAccount(params),
    onSuccess: (editedCardSourceAccount, params) => {
      queryClient.setQueryData(
        queryKeys.paymentMethods({ partyId: params.partyId }),
        (oldData: GetPaymentMethodsResponse | undefined) => {
          if (oldData === undefined) {
            return;
          }

          return getUpdatedSourceAccountsWithEdit(
            editedCardSourceAccount,
            params.sourceAccountId,
            oldData,
          );
        },
      );
      queryClient.invalidateQueries(
        queryKeys.paymentMethods({ partyId: params.partyId }),
      );
    },
  });

export const useMakeOneTimePayment = () =>
  useMutation({
    mutationFn: (params: MakeOneTimePaymentRequest) =>
      makeOneTimePayment(params),
    onSuccess: (_data, { dwbuid }) => {
      queryClient.invalidateQueries(queryKeys.accountDetails(dwbuid));
      queryClient.invalidateQueries(queryKeys.paymentTransactions());
    },
  });

export const useMakeSeriesPayment = () =>
  useMutation({
    mutationFn: (params: MakeSeriesPaymentRequest) => makeSeriesPayment(params),
    onSuccess: (_data, { dwbuid }) => {
      queryClient.invalidateQueries(queryKeys.accountDetails(dwbuid));
      queryClient.invalidateQueries(queryKeys.paymentTransactions());
    },
  });

export const useGetPaymentSeriesSchedule = () =>
  useMutation({
    mutationFn: (params: GetPaymentSeriesScheduleRequest) =>
      getPaymentSeriesSchedule(params),
  });

export const useGetPartyRestrictions = (partyId: string) =>
  useQuery({
    queryKey: queryKeys.partyRestrictions(partyId),
    queryFn: () => getPartyRestrictions(partyId),
  });

export const useGetBankNames = (abaNumber: string) =>
  useQuery({
    enabled: !!abaNumber,
    queryKey: queryKeys.bankNames(abaNumber),
    queryFn: () => getBankNames(abaNumber),
  });

export const useAddBankRestriction = () =>
  useMutation({
    mutationFn: (params: AddRestrictionRequest) => addBankRestriction(params),
    async onMutate(params) {
      await queryClient.cancelQueries(
        queryKeys.partyRestrictions(params.partyId),
      );

      const previousRestrictions: GetPartyRestrictionsResponse | undefined =
        queryClient.getQueryData(queryKeys.partyRestrictions(params.partyId));
      queryClient.setQueryData(
        queryKeys.partyRestrictions(params.partyId),
        (old: GetPartyRestrictionsResponse | undefined) => {
          if (!old) {
            return;
          }
          return {
            ...old,
            partyBankRestriction:
              params.restriction === RESTRICTION_NONE
                ? []
                : [{ restriction: params.restriction }],
          };
        },
      );
      queryClient.invalidateQueries(
        queryKeys.paymentMethods({ partyId: params.partyId }),
      );

      return { previousRestrictions };
    },
    onError(_error, { partyId }, context) {
      queryClient.setQueryData(
        queryKeys.partyRestrictions(partyId),
        context?.previousRestrictions,
      );
    },
  });

export const useAddCardRestriction = () =>
  useMutation({
    mutationFn: (params: AddRestrictionRequest) => addCardRestriction(params),
    async onMutate(params) {
      await queryClient.cancelQueries(
        queryKeys.partyRestrictions(params.partyId),
      );

      const previousRestrictions: GetPartyRestrictionsResponse | undefined =
        queryClient.getQueryData(queryKeys.partyRestrictions(params.partyId));
      queryClient.setQueryData(
        queryKeys.partyRestrictions(params.partyId),
        (old: GetPartyRestrictionsResponse | undefined) => {
          if (!old) {
            return;
          }
          return {
            ...old,
            partyCardRestriction:
              params.restriction === RESTRICTION_NONE
                ? []
                : [{ restriction: params.restriction }],
          };
        },
      );
      queryClient.invalidateQueries(
        queryKeys.paymentMethods({ partyId: params.partyId }),
      );

      return { previousRestrictions };
    },
    onError(_error, { partyId }, context) {
      queryClient.setQueryData(
        queryKeys.partyRestrictions(partyId),
        context?.previousRestrictions,
      );
    },
  });

export const useCancelPayment = () =>
  useMutation({
    mutationFn: cancelPayment,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.paymentTransactions());
    },
  });

export const useEditOneTimePayment = () =>
  useMutation({
    mutationFn: editOneTimePayment,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.paymentTransactions());
    },
  });

export const useEditSeriesPayment = () =>
  useMutation({
    mutationFn: editSeriesPayment,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.paymentTransactions());
    },
  });

export const useCancelSeriesPayment = () =>
  useMutation({
    mutationFn: cancelSeriesPayment,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.paymentTransactions());
    },
  });

export const useGetPermissionToDiscuss = (req: GetPermissionToDiscussRequest) =>
  useQuery({
    queryKey: queryKeys.permissionToDiscuss(req),
    queryFn: () => getPermissionToDiscuss(req),
    select: (data) => {
      const relationshipTypesById = data.relationshipTypes.reduce(
        (relationById, curr) => {
          relationById[curr.id] = curr;
          return relationById;
        },
        {} as Record<string, PermissionToDiscussRelationshipType>,
      );
      const permissionsById = data.permissionsList.reduce(
        (permissionById, curr) => {
          permissionById[curr.id] = curr;
          return permissionById;
        },
        {} as Record<string, PermissionToDiscussPermission>,
      );

      return {
        lastVerifiedDate: data.lastVerifiedDate,
        permissionsList: data.permissionsList,
        relationshipTypesById,
        permissionsById,
      };
    },
  });

export const useAddPermissionToDiscuss = (
  req: GetPermissionToDiscussRequest,
  customerInfoReq: GetCustomerInfoRequest,
) =>
  useMutation({
    mutationFn: addPermissionToDicuss,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.permissionToDiscuss(req));
      queryClient.invalidateQueries(queryKeys.customerInfo(customerInfoReq));
    },
  });

export const useEditPermissionToDiscuss = (
  req: GetPermissionToDiscussRequest,
  customerInfoReq: GetCustomerInfoRequest,
) =>
  useMutation({
    mutationFn: editPermissionToDicuss,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.permissionToDiscuss(req));
      queryClient.invalidateQueries(queryKeys.customerInfo(customerInfoReq));
    },
  });

export const useVerifyPermissionToDiscuss = (
  req: GetPermissionToDiscussRequest,
  customerInfoReq: GetCustomerInfoRequest,
) =>
  useMutation({
    mutationFn: verifyPermissionToDiscuss,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.permissionToDiscuss(req));
      queryClient.invalidateQueries(queryKeys.customerInfo(customerInfoReq));
    },
  });

export const useRemovePermissionToDicuss = (
  req: GetPermissionToDiscussRequest,
  customerInfoReq: GetCustomerInfoRequest,
) =>
  useMutation({
    mutationFn: removePermissionToDiscuss,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.permissionToDiscuss(req));
      queryClient.invalidateQueries(queryKeys.customerInfo(customerInfoReq));
    },
  });

export const useGetCycleToDateTransactions = (dwbuid: string) =>
  useQuery({
    queryKey: queryKeys.cycleToDateTransactions(dwbuid),
    queryFn: () => getCycleToDateTransactions(dwbuid),
  });

export const useGetStatementHistory = (dwbuid: string) =>
  useQuery({
    queryKey: queryKeys.statementHistory(dwbuid),
    queryFn: () => getStatementHistory(dwbuid),
  });

export const useGetLostStolenOptions = (dwbuid: string) =>
  useQuery({
    queryKey: queryKeys.lostStolenOptions(dwbuid),
    queryFn: () => getLostStolenOptions(dwbuid),
  });

export const useSubmitLostStolen = () =>
  useMutation({
    mutationFn: (params: SubmitLostStolenRequest) => submitLostStolen(params),
    onSuccess: (_data, { dwbuid }) => {
      queryClient.invalidateQueries(queryKeys.accountDetails(dwbuid));
    },
  });

export const useGetCreditLineIncreaseEligibility = (
  req: GetCreditLineIncreaseEligibilityRequest,
) =>
  useQuery({
    queryKey: queryKeys.creditLineHistoryEligibility(req),
    queryFn: () => getCreditLineIncreaseEligibility(req),
  });

export const useSubmitCreditLineIncrease = () =>
  useMutation({
    mutationFn: submitCreditLineIncrease,
    onSuccess: (_, req) => {
      queryClient.invalidateQueries(
        queryKeys.creditLineHistoryEligibility({
          dwbuid: req.dwbuid,
          referenceId: req.referenceId,
          channel: req.channel,
        }),
      );
    },
  });

export const useGetCallInfo = (req: GetCallInfoRequest) =>
  useQuery({
    queryKey: queryKeys.callInfo(req),
    enabled: !!req.partyId && !!req.dwbuid,
    queryFn: () => getCallInfo(req),
  });

export const useGetTerms = (dwbuid: string) =>
  useQuery({
    queryKey: queryKeys.terms(dwbuid),
    queryFn: () => getTerms(dwbuid),
  });

export const useGetPhoneOptions = () =>
  useQuery({
    queryKey: queryKeys.phoneOptions(),
    queryFn: () => getPhoneOptions(),
  });

export const useGetPhones = (dwbuid: string) =>
  useQuery({
    queryKey: queryKeys.phones(dwbuid),
    queryFn: () => getPhones(dwbuid),
    select: (data) => {
      const formattedPrimaryPhone = data.primaryPhone.replace(
        numericsOnlyRegex,
        "",
      );
      const formattedSecondaryPhone = data.secondaryPhone.replace(
        numericsOnlyRegex,
        "",
      );

      return {
        ...data,
        primaryPhone:
          formattedPrimaryPhone.length > 0
            ? formattedPrimaryPhone
            : strings.defaultPhoneNumber,
        secondaryPhone:
          formattedSecondaryPhone.length > 0
            ? formattedSecondaryPhone
            : strings.defaultPhoneNumber,
      };
    },
  });

export const useVerifyPhones = (dwbuid: string) =>
  useMutation({
    mutationFn: verifyPhones,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.phones(dwbuid));
    },
  });

export const useAddPhone = (partyId: string) =>
  useMutation({
    mutationFn: addPhone,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.phones(partyId));
    },
  });

export const useEditPhone = (dwbuid: string) =>
  useMutation({
    mutationFn: editPhone,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.phones(dwbuid));
    },
  });

export const useGetInactivationChangeMessages = () =>
  useMutation({
    mutationFn: getInactivationChangeMessages,
  });

export const useAccountSearch = () =>
  useMutation({
    mutationFn: accountSearch,
  });

export const useAccountSearchByThreeOfFour = () =>
  useMutation({
    mutationFn: accountSearchByThreeOfFour,
  });

export const useGetCitiesByZipcode = (zipcode: string) =>
  useQuery({
    queryKey: queryKeys.cities(zipcode),
    queryFn: () => getCitiesByZipcode(zipcode),
    enabled: !!zipcode,
  });

export const useGetCustomerInfo = (req: GetCustomerInfoRequest) =>
  useQuery({
    queryKey: queryKeys.customerInfo(req),
    queryFn: () => getCustomerInfo(req),
    cacheTime: 0,
  });

export const useUpdateCustomerInfo = (dwbuid: string, referenceId?: string) =>
  useMutation({
    mutationFn: updateCustomerInfo,
    onSettled: () => {
      queryClient.invalidateQueries(
        queryKeys
          .changedHistory({
            dwbuid,
            referenceId,
          })
          .slice(0, 3),
      );
      queryClient.invalidateQueries(
        queryKeys
          .verifiedHistory({
            dwbuid,
            referenceId,
          })
          .slice(0, 3),
      );
      queryClient.invalidateQueries(queryKeys.paperlessSettings({ dwbuid }));
    },
  });

export const useGetChangedHistory = (req: GetChangedHistoryRequest) =>
  useQuery({
    queryKey: queryKeys.changedHistory(req),
    queryFn: () => getChangedHistory(req),
  });

export const useGetVerifiedHistory = (req: GetVerifiedHistoryRequest) =>
  useQuery({
    queryKey: queryKeys.verifiedHistory(req),
    queryFn: () => getVerifiedHistory(req),
  });

export const useGetStatements = (req: GetStatementsRequest) =>
  useQuery({
    queryKey: queryKeys.statements(req),
    queryFn: () => getStatements(req),
  });

export const useGetPaperlessSettings = (req: GetPaperlessSettingsRequest) =>
  useQuery({
    queryKey: queryKeys.paperlessSettings(req),
    queryFn: () => getPaperlessSettings(req),
  });

export const useUpdatePaperlessSettings = () =>
  useMutation({
    mutationFn: (params: PatchPaperlessSettingsRequest) =>
      updatePaperlessSettings(params),
  });

export const useGetCardActivationInfo = (dwbuid: string) =>
  useQuery({
    queryKey: queryKeys.cardActivationInfo(dwbuid),
    queryFn: () => getCardActivationInfo(dwbuid),
  });

export const useSubmitCardActivation = () =>
  useMutation({
    mutationFn: submitCardActivation,
    onSuccess: (data, { dwbuid }) => {
      queryClient.invalidateQueries(queryKeys.accountDetails(dwbuid));
      queryClient.setQueryData(
        queryKeys.cardActivationInfo(dwbuid),
        (oldData: GetCardActivationInfoResponse | undefined) => {
          if (oldData === undefined) {
            return undefined;
          }

          return { ...oldData, ...data, showFields: false, activated: true };
        },
      );
    },
  });

export const useGetTextBankingServices = (dwbuid: string) =>
  useQuery({
    enabled: !!dwbuid,
    queryKey: queryKeys.textBankingServices(dwbuid),
    queryFn: () => getTextBankingServices(dwbuid),
  });

export const useStopFteuService = () =>
  useMutation({
    mutationFn: stopFteuService,
  });

export const useStopFraudAlertService = () =>
  useMutation({
    mutationFn: stopFraudAlertService,
  });

export const useGetResultOptions = (channel: Channel) =>
  useQuery({
    queryKey: queryKeys.getResultOptions(channel),
    queryFn: () => getResultOptions(channel),
  });

export const useUpdatePermCollQueue = () =>
  useMutation({
    mutationFn: (req: UpdatePermCollQueueRequest) => updatePermCollQueue(req),
  });

export const useSubmitResultCsr = () =>
  useMutation({
    mutationFn: (req: ResultCsrRequest) => submitResultCsr(req),
  });

export const useSubmitResultNoPayment = () =>
  useMutation({
    mutationFn: (req: ResultNoPaymentRequest) => submitResultNoPayment(req),
  });

export const useSubmitResultPayment = () =>
  useMutation({
    mutationFn: (req: ResultPaymentRequest) => submitResultPayment(req),
  });

export const useSubmitResultResearchOnly = () =>
  useMutation({
    mutationFn: (req: ResultResearchOnlyRequest) =>
      submitResultResearchOnly(req),
  });

export const useGetNotesSearch = (req: GetNotesSearchRequest) =>
  useQuery({
    queryKey: queryKeys.getNotesSearch(req),
    queryFn: () => getNotesSearch(req),
  });

export const useGetNoteDetails = (dwbuid: string, noteId: string) =>
  useQuery({
    queryKey: queryKeys.noteDetails(dwbuid, noteId),
    queryFn: () => getNoteDetails(dwbuid, noteId),
  });

export const useAddNote = () =>
  useMutation({
    mutationFn: (req: AddNoteRequest) => addNote(req),
    onSuccess: (_, { dwbuid }) => {
      queryClient.invalidateQueries([getNotesSearchQueryKeyName, dwbuid]);
    },
  });

// using useMutation here to only call it when we want to
export const useGetListen = () =>
  useMutation({
    mutationFn: (initialTime: Date | null) => getListen(initialTime),
  });

export const useCallEnded = () =>
  useMutation({
    mutationFn: (req: CallEndedRequest) => callEnded(req),
  });
